const { SITE_BASE_URL, buildAbsoluteSiteUrl } = require('./brokerConnectionSource');
const { CORE_SUBSCRIPTION_PLANS } = require('./publicPricingSource');
const { articleAuthorFor } = require('./editorialContent');
const { ogImageUrlForPath } = require('./ogTitleCard');

function absoluteUrl(value, baseUrl = SITE_BASE_URL) {
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  return buildAbsoluteSiteUrl(value, baseUrl);
}

function breadcrumbName(pathname, title) {
  if (pathname === '/') return 'Home';
  if (title) return title.replace(/\s+\|\s+BotSpot.*$/i, '');
  return pathname.split('/').filter(Boolean).pop().replace(/-/g, ' ');
}

function buildBreadcrumbs(route, baseUrl = SITE_BASE_URL) {
  const parts = route.path.split('/').filter(Boolean);
  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: baseUrl,
    },
  ];

  let currentPath = '';
  parts.forEach((part, index) => {
    currentPath += `/${part}`;
    const isLast = index === parts.length - 1;
    // These URL namespaces have no published page of their own.
    if (currentPath === '/courses' || currentPath === '/marketplace/strategy') return;
    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      name: isLast
        ? breadcrumbName(currentPath, route.content?.heading || route.title)
        : breadcrumbName(currentPath),
      item:
        isLast && route.canonical
          ? absoluteUrl(route.canonical, baseUrl)
          : buildAbsoluteSiteUrl(currentPath, baseUrl),
    });
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

function organizationId(baseUrl = SITE_BASE_URL) {
  return `${buildAbsoluteSiteUrl('/', baseUrl)}#organization`;
}

function organizationForHomepage(baseUrl = SITE_BASE_URL) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': organizationId(baseUrl),
    name: 'Lumiwealth, Inc.',
    alternateName: 'BotSpot',
    url: buildAbsoluteSiteUrl('/', baseUrl),
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'contact@botspot.trade',
      url: buildAbsoluteSiteUrl('/contact', baseUrl),
    },
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'US',
    },
  };
}

function softwareApplicationForHomepage(route, baseUrl = SITE_BASE_URL) {
  const prices = CORE_SUBSCRIPTION_PLANS.map((tier) => tier.price).filter((price) =>
    Number.isFinite(price),
  );
  const lowPrice = Math.min(...prices);
  const highPrice = Math.max(...prices);
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'BotSpot',
    applicationCategory: 'FinanceApplication',
    operatingSystem: 'Web, iOS, Android',
    url: absoluteUrl(route.canonical || baseUrl, baseUrl),
    description: route.description,
    provider: { '@id': organizationId(baseUrl) },
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      lowPrice: Number(lowPrice.toFixed(2)),
      highPrice: Number(highPrice.toFixed(2)),
      offerCount: CORE_SUBSCRIPTION_PLANS.length,
    },
  };
}

function softwareApplicationForStrategy(route, baseUrl = SITE_BASE_URL) {
  const canonical = absoluteUrl(route.canonical || route.path, baseUrl);
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: route.content?.heading || route.title,
    applicationCategory: 'FinanceApplication',
    operatingSystem: 'Web',
    url: canonical,
    description: route.description,
    isPartOf: {
      '@type': 'SoftwareApplication',
      name: 'BotSpot',
      url: baseUrl,
    },
  };
}

function structuredDataForRoute(route, baseUrl = SITE_BASE_URL) {
  const canonical = absoluteUrl(route.canonical || route.path, baseUrl);
  const isArticle = route.pageType === 'article';
  const isEvent = route.pageType === 'event';
  const isHomepage = route.path === '/';
  const isMarketplaceStrategy = Boolean(route.path?.startsWith('/marketplace/strategy/'));
  let primary;
  if (isHomepage) {
    primary = softwareApplicationForHomepage(route, baseUrl);
  } else if (isMarketplaceStrategy || route.schemaType === 'SoftwareApplication') {
    primary = softwareApplicationForStrategy(route, baseUrl);
  } else if (isEvent) {
    primary = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: route.name || route.content?.heading || route.title,
      description: route.description,
      url: canonical,
      image: route.image ? [absoluteUrl(route.image, baseUrl)] : undefined,
      startDate: route.startDate,
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
      location: {
        '@type': 'VirtualLocation',
        url: canonical,
      },
      organizer: {
        '@type': 'Organization',
        name: 'BotSpot',
        url: baseUrl,
      },
    };
  } else {
    primary = {
      '@context': 'https://schema.org',
      '@type': isArticle ? 'Article' : 'WebPage',
      headline: route.content?.heading || route.title,
      name: route.title,
      description: route.description,
      url: canonical,
      mainEntityOfPage: canonical,
      publisher: {
        '@type': 'Organization',
        name: 'BotSpot',
        url: baseUrl,
      },
    };
  }

  if (isArticle && primary['@type'] === 'Article') {
    primary.author = articleAuthorFor(route.author, baseUrl);
    primary.image = [absoluteUrl(route.image || ogImageUrlForPath(route.path, baseUrl), baseUrl)];
    // A source-review date or sitemap lastmod is not an article publication date.
    if (route.publishedAt) primary.datePublished = route.publishedAt;
    if (route.modifiedAt) primary.dateModified = route.modifiedAt;
  }
  if (
    Array.isArray(route.content?.sources) &&
    route.content.sources.length > 0 &&
    primary['@type'] !== 'SoftwareApplication'
  ) {
    primary.citation = route.content.sources.map((source) => absoluteUrl(source.url, baseUrl));
  }

  const graph = [primary];

  if (route.path === '/') {
    graph.push(organizationForHomepage(baseUrl));
  } else {
    graph.push(buildBreadcrumbs(route, baseUrl));
  }

  if (Array.isArray(route.content?.faq) && route.content.faq.length > 0) {
    graph.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: route.content.faq.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });
  }

  return graph;
}

const SCHEMA_ORG = 'https://schema.org';

const dollars = (cents) => Math.round(Number(cents)) / 100;

function isoString(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** Calendar date in Eastern time, the zone the page shows every date in. */
function easternDate(value) {
  const iso = isoString(value);
  if (!iso) return null;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

function botspotProvider(baseUrl = SITE_BASE_URL) {
  return { '@type': 'Organization', name: 'BotSpot', url: baseUrl };
}

/**
 * Course, CourseInstance and Offer schema for a live cohort, built from the
 * same normalized catalog plans and session rows the course page renders
 * (useCourseCatalog). Amounts are cents. Anything missing is left out rather
 * than filled in: no plans means no offers, no sessions means no instance.
 */
function courseStructuredData({
  path,
  name,
  description,
  plans = [],
  sessions = [],
  promoEndsAt = null,
  isEnrollable = false,
  baseUrl = SITE_BASE_URL,
}) {
  const url = buildAbsoluteSiteUrl(path, baseUrl);
  const course = {
    '@context': SCHEMA_ORG,
    '@type': 'Course',
    '@id': `${url}#course`,
    name,
    description,
    url,
    provider: botspotProvider(baseUrl),
  };
  const dated = sessions
    .map((session) => isoString(session?.scheduledAt))
    .filter(Boolean)
    .sort();
  if (dated.length > 0) {
    course.hasCourseInstance = [
      {
        '@type': 'CourseInstance',
        courseMode: 'Online',
        startDate: dated[0],
        endDate: dated[dated.length - 1],
        location: { '@type': 'VirtualLocation', url },
      },
    ];
  }
  const priceValidUntil = easternDate(promoEndsAt);
  const offers = plans
    .filter((plan) => Number.isFinite(plan?.totalAmount))
    .map((plan) => {
      const offer = {
        '@type': 'Offer',
        name: plan.label,
        category: 'Paid',
        price: dollars(plan.totalAmount),
        priceCurrency: 'USD',
        availability: isEnrollable ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut',
        url,
      };
      if (isEnrollable && priceValidUntil) offer.priceValidUntil = priceValidUntil;
      const specification = [];
      if (plan.installments > 1) {
        specification.push({
          '@type': 'UnitPriceSpecification',
          price: dollars(plan.amount),
          priceCurrency: 'USD',
          billingDuration: plan.installments,
          unitCode: 'MON',
        });
      }
      if (plan.hasDiscount && Number.isFinite(plan.originalTotalAmount)) {
        specification.push({
          '@type': 'UnitPriceSpecification',
          priceType: 'https://schema.org/ListPrice',
          price: dollars(plan.originalTotalAmount),
          priceCurrency: 'USD',
        });
      }
      if (specification.length > 0) offer.priceSpecification = specification;
      return offer;
    });
  if (offers.length > 0) course.offers = offers;
  return course;
}

/**
 * Event schema for the next public challenge, built from the challenge record
 * the page renders. Returns null without a start date: no scheduled run, no
 * event.
 */
function challengeEventStructuredData({
  path,
  name,
  description,
  startDate,
  isFree = false,
  image = null,
  baseUrl = SITE_BASE_URL,
}) {
  const start = isoString(startDate);
  if (!start) return null;
  const url = buildAbsoluteSiteUrl(path, baseUrl);
  const event = {
    '@context': SCHEMA_ORG,
    '@type': 'Event',
    name,
    description,
    url,
    startDate: start,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    location: { '@type': 'VirtualLocation', url },
    organizer: botspotProvider(baseUrl),
  };
  if (image) event.image = [absoluteUrl(image, baseUrl)];
  if (isFree) {
    event.isAccessibleForFree = true;
    event.offers = {
      '@type': 'Offer',
      price: 0,
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url,
    };
  }
  return event;
}

module.exports = {
  buildBreadcrumbs,
  challengeEventStructuredData,
  courseStructuredData,
  organizationForHomepage,
  softwareApplicationForHomepage,
  softwareApplicationForStrategy,
  structuredDataForRoute,
};
